import { useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AuthTabs, type AuthTab } from '@/components/login/AuthTabs';
import { GoogleSignInButton } from '@/components/login/GoogleSignInButton';
import { LoginForm } from '@/components/login/LoginForm';
import { RegisterForm } from '@/components/login/RegisterForm';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/useToast';
import { t } from '@/i18n';
import {
  clearPostLoginRedirect,
  readPostLoginRedirect,
} from '@/utils/postLoginRedirect';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const [tab, setTab] = useState<AuthTab>('login');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { status } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const googleFailed = searchParams.has('googleError');

  // The backend sends failed Google redirect sign-ins back here with a flag.
  useEffect(() => {
    if (!googleFailed) return;
    showToast(t.auth.googleLoginFailed, 'error');
    setSearchParams(
      (params) => {
        params.delete('googleError');
        return params;
      },
      { replace: true },
    );
  }, [googleFailed, setSearchParams, showToast]);

  useEffect(() => {
    if (status === 'authenticated') clearPostLoginRedirect();
  }, [status]);

  if (status === 'authenticated') {
    return <Navigate to={readPostLoginRedirect() ?? '/'} replace />;
  }

  const form = (
    <div className={styles.form}>
      <GoogleSignInButton />
      <div className={styles.divider}>
        <span>{t.auth.orEmail}</span>
      </div>
      <AuthTabs value={tab} onChange={setTab} />
      {tab === 'login' ? <LoginForm /> : <RegisterForm />}
    </div>
  );

  const mobileAuthCard = (
    <div className={styles.authCard}>
      <div className={styles.panel}>
        <div className={styles.panelIcon} aria-hidden>
          {String.fromCodePoint(0x1f52e)}
        </div>
        <h1 className={styles.panelTitle}>{t.auth.loginHeroTitle}</h1>
        <p className={styles.panelSub}>{t.auth.loginHeroSub}</p>
      </div>
      {form}
    </div>
  );

  if (isDesktop) {
    return (
      <div className={styles.page}>
        <div className={styles.desktop}>
          <div className={styles.desktopLeft}>
            <div className={styles.leftInner}>
              <div className={styles.leftTitle}>{t.auth.loginHeroTitle}</div>
              <p className={styles.leftSub}>{t.auth.loginHeroSub}</p>
              <ul className={styles.bullets}>
                <li className={styles.bullet}>
                  <span className={styles.bIco} aria-hidden>
                    {String.fromCodePoint(0x1f4f8)}
                  </span>
                  <span>{t.auth.loginBullet1}</span>
                </li>
                <li className={styles.bullet}>
                  <span className={styles.bIco} aria-hidden>
                    {String.fromCodePoint(0x1f3af)}
                  </span>
                  <span>{t.auth.loginBullet2}</span>
                </li>
                <li className={styles.bullet}>
                  <span className={styles.bIco} aria-hidden>
                    {String.fromCodePoint(0x1f4da)}
                  </span>
                  <span>{t.auth.loginBullet3}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className={styles.desktopRight}>
            <div className={styles.rightInner}>
              <h1 className={styles.rTitle}>{t.auth.loginTitle}</h1>
              {form}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.mobileWrap}>{mobileAuthCard}</div>
    </div>
  );
}
